import type { components, operations } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '@/lib/api'

/**
 * The typed boundary to `/purchasing/landed-costs` (landed costs ticket 03).
 *
 * Every amount is the server's decimal string. The split itself — each line's
 * share — is the backend's; the screens only display it, and the post dialog's
 * three figures are a labelled preview (AGENTS.md §3).
 */
export type LandedCost = components['schemas']['LandedCostResource']
export type LandedCostLine = components['schemas']['LandedCostLineResource']
export type LandedCostBody = components['schemas']['LandedCostRequest']
export type LandedCostStatus = 'draft' | 'posted' | 'cancelled'
export type SplitMethod = LandedCostBody['split_method']

/** A preview line. `on_hand` is null when a receipt predates the costing cutover. */
export type LandedCostPreviewLine = Omit<
  components['schemas']['LandedCostPreviewLineResource'],
  'on_hand'
> & { on_hand: string | null }

export const LANDED_COST_STATUSES: LandedCostStatus[] = ['draft', 'posted', 'cancelled']
export const SPLIT_METHODS: SplitMethod[] = ['value', 'quantity', 'equal', 'manual']

export interface LandedCostFilters {
  status?: LandedCostStatus
  supplier?: string
  category?: string
  order?: string
  page?: number
  perPage?: number
}

type LandedCostListResponse =
  operations['landedCost.index']['responses'][200]['content']['application/json']

/** One page of the list, with the paginator's meta as the API sends it. */
export type LandedCostPage = {
  data: LandedCost[]
  meta: Pick<LandedCostListResponse['meta'], 'current_page' | 'last_page' | 'per_page' | 'total'>
}

export const LANDED_COST_PAGE_SIZE = 25

export const landedCostKeys = {
  all: (companyId: string | null) =>
    ['companies', companyId, 'purchasing', 'landed-costs'] as const,
  list: (companyId: string | null, filters: LandedCostFilters) =>
    [...landedCostKeys.all(companyId), 'list', filters] as const,
  detail: (companyId: string | null, id: string) =>
    [...landedCostKeys.all(companyId), 'detail', id] as const,
  preview: (companyId: string | null, id: string) =>
    [...landedCostKeys.all(companyId), 'preview', id] as const,
}

function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

export function useLandedCosts(companyId: string | null, filters: LandedCostFilters) {
  return useQuery({
    queryKey: landedCostKeys.list(companyId, filters),
    enabled: Boolean(companyId),
    queryFn: async (): Promise<LandedCostPage> => {
      // `page` is read by Laravel's paginator; the index request does not declare it.
      const query = {
        status: filters.status,
        supplier_id: filters.supplier,
        landed_cost_category_id: filters.category,
        purchase_order_id: filters.order,
        per_page: filters.perPage ?? LANDED_COST_PAGE_SIZE,
        page: filters.page,
      }
      const { data } = await api.GET('/purchasing/landed-costs', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { query },
      })
      if (!data) throw new Error('GET /purchasing/landed-costs returned no body')
      return { data: data.data, meta: data.meta }
    },
  })
}

export function useLandedCost(companyId: string | null, id: string | null) {
  return useQuery({
    queryKey: landedCostKeys.detail(companyId, id ?? ''),
    enabled: Boolean(companyId && id),
    queryFn: async (): Promise<LandedCost> => {
      const { data } = await api.GET('/purchasing/landed-costs/{landedCost}', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { landedCost: id ?? '' } },
      })
      if (!data) throw new Error('GET /purchasing/landed-costs/{landedCost} returned no body')
      return data.data
    },
  })
}

export function useLandedCostPreview(companyId: string | null, id: string, enabled: boolean) {
  return useQuery({
    queryKey: landedCostKeys.preview(companyId, id),
    enabled: Boolean(companyId) && enabled,
    queryFn: async (): Promise<LandedCostPreviewLine[]> => {
      const { data } = await api.GET('/purchasing/landed-costs/{landedCost}/preview', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { landedCost: id } },
      })
      return data?.data ?? []
    },
  })
}

type SaveVariables = { id: string | null; body: LandedCostBody; idempotencyKey: string }

/** Create (no id, idempotent) or replace (id) a draft landed cost. */
export function useSaveLandedCost(
  companyId: string | null,
  options?: Omit<UseMutationOptions<LandedCost, Error, SaveVariables>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const company = useFreshCompanyId(companyId)
  return useMutation<LandedCost, Error, SaveVariables>({
    ...options,
    mutationFn: async ({ id, body, idempotencyKey }) => {
      if (id === null) {
        const { data } = await api.POST('/purchasing/landed-costs', {
          headers: { 'X-Company-Id': company(), 'Idempotency-Key': idempotencyKey },
          body,
        })
        if (!data) throw new Error('POST /purchasing/landed-costs returned no body')
        return data.data
      }
      const { data } = await api.PATCH('/purchasing/landed-costs/{landedCost}', {
        headers: { 'X-Company-Id': company() },
        params: { path: { landedCost: id } },
        body,
      })
      if (!data) throw new Error('PATCH /purchasing/landed-costs/{landedCost} returned no body')
      return data.data
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: landedCostKeys.all(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

type TransitionVariables = { id: string; idempotencyKey: string; reason?: string }

function useTransition(
  companyId: string | null,
  kind: 'post' | 'cancel',
  options?: Omit<UseMutationOptions<LandedCost, Error, TransitionVariables>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const company = useFreshCompanyId(companyId)
  return useMutation<LandedCost, Error, TransitionVariables>({
    ...options,
    mutationFn: async ({ id, idempotencyKey, reason }) => {
      const init = {
        headers: { 'X-Company-Id': company(), 'Idempotency-Key': idempotencyKey },
        params: { path: { landedCost: id } },
      }
      const { data } =
        kind === 'post'
          ? await api.POST('/purchasing/landed-costs/{landedCost}/post', init)
          : await api.POST('/purchasing/landed-costs/{landedCost}/cancel', {
              ...init,
              body: { reason: reason || null },
            })
      if (!data)
        throw new Error(`POST /purchasing/landed-costs/{landedCost}/${kind} returned no body`)
      return data.data
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: landedCostKeys.all(companyId) })
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'purchasing', 'purchase-orders'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export function usePostLandedCost(
  companyId: string | null,
  options?: Omit<UseMutationOptions<LandedCost, Error, TransitionVariables>, 'mutationFn'>,
) {
  return useTransition(companyId, 'post', options)
}

export function useCancelLandedCost(
  companyId: string | null,
  options?: Omit<UseMutationOptions<LandedCost, Error, TransitionVariables>, 'mutationFn'>,
) {
  return useTransition(companyId, 'cancel', options)
}

export function useDeleteLandedCost(
  companyId: string | null,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const company = useFreshCompanyId(companyId)
  return useMutation<void, Error, string>({
    ...options,
    mutationFn: async (id) => {
      await api.DELETE('/purchasing/landed-costs/{landedCost}', {
        headers: { 'X-Company-Id': company() },
        params: { path: { landedCost: id } },
      })
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: landedCostKeys.all(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}
